import React, { useState } from "react";
import { api } from "../api/client.js";
import { colors } from "../utils/theme.js";
import { inputStyle, primaryBtn, ghostBtn, labelStyle } from "../clients/ui.js";
import ConfirmDialog from "../clients/ConfirmDialog.jsx";
import { STATUS_META, isClientOwner } from "./crmLogic.js";
import { CONTACT_INPUTS, splitTags } from "./ContactForm.jsx";
import UserAvatar from "./UserAvatar.jsx";
import Modal from "./Modal.jsx";

export const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.lead;
  return <span style={{ fontSize: 11.5, fontWeight: 600, padding: "3px 9px", borderRadius: 10, whiteSpace: "nowrap", color: m.color, background: m.bg }}>{m.label}</span>;
};

// One contact from the shared book: edit shared fields, see every client's track with them,
// link them to another client, or delete them (which removes every client's track too).
export default function ContactCard({ contact, tracks = [], clients = [], users = [], onChanged, onClose, flash }) {
  const [form, setForm] = useState({ ...Object.fromEntries(CONTACT_INPUTS.map(([k]) => [k, contact[k] || ""])), expertise: (contact.expertise || []).join(", ") });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [linkTo, setLinkTo] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const clientName = (id) => clients.find((c) => c.id === id)?.name || "Unknown client";
  const user = (id) => users.find((u) => u.id === id || u.email === id);
  const linked = new Set(tracks.map((t) => t.clientId));

  const save = async () => {
    if (!form.name.trim()) return setError("Name is required.");
    setBusy(true); setError(null);
    const r = await api.contacts.update(contact.id, { ...form, expertise: splitTags(form.expertise) });
    setBusy(false);
    if (r.ok) { flash?.("success", "Contact saved"); onChanged(); return; }
    setError(r.status === 409 ? "Another contact in the book already has that email." : r.data?.error || "Couldn’t save.");
  };
  const link = async () => {
    setBusy(true);
    const r = await api.crmTracks.create({ contactId: contact.id, clientId: linkTo });
    setBusy(false);
    if (r.ok) { flash?.("success", `Added to ${clientName(linkTo)}’s CRM`); setLinkTo(""); onChanged(); }
    else setError(r.status === 409 ? "Already on that client’s CRM." : "Couldn’t link to that client.");
  };
  const del = async () => {
    setConfirmDelete(false); setBusy(true);
    const r = await api.contacts.remove(contact.id);
    setBusy(false);
    if (r.ok) { flash?.("success", `Deleted ${contact.name}`); onChanged(); onClose(); }
    else setError("Couldn’t delete.");
  };

  return (
    <Modal title={contact.name} width={620} onClose={onClose}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {CONTACT_INPUTS.map(([k, label]) => (
          <label key={k} style={{ display: "flex", flexDirection: "column", gap: 4, gridColumn: k === "expertise" ? "1 / -1" : undefined }}>
            <span style={labelStyle}>{label}</span>
            <input aria-label={label} style={inputStyle} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} />
          </label>
        ))}
      </div>
      {error && <div style={{ color: colors.danger, fontSize: 12.5, marginTop: 8 }}>{error}</div>}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14 }}>
        <button onClick={() => setConfirmDelete(true)} disabled={busy} style={{ ...ghostBtn, color: colors.danger, borderColor: "rgba(239,68,68,0.4)" }}>Delete contact</button>
        <button onClick={save} disabled={busy} style={primaryBtn}>Save</button>
      </div>

      <div style={{ ...labelStyle, marginTop: 22, marginBottom: 8 }}>Clients working this contact</div>
      {tracks.length === 0 && <div style={{ fontSize: 13, color: colors.textDim }}>Not linked to any client yet.</div>}
      {tracks.map((t) => (
        <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: `1px solid ${colors.border}` }}>
          <span style={{ fontSize: 13, color: colors.text, flex: 1 }}>{clientName(t.clientId)}</span>
          <StatusPill status={t.status} />
          {isClientOwner(t.ownerUserId) ? <span style={{ fontSize: 10, fontWeight: 700, color: colors.warning, letterSpacing: 0.4 }}>CLIENT</span>
            : t.ownerUserId ? <UserAvatar user={user(t.ownerUserId)} showName /> : <span style={{ fontSize: 11, color: colors.textDim }}>Unassigned</span>}
        </div>
      ))}
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <select aria-label="Link to client" style={{ ...inputStyle, width: "auto", flex: 1 }} value={linkTo} onChange={(e) => setLinkTo(e.target.value)}>
          <option value="">Link to client…</option>
          {clients.filter((c) => !c.archived && !linked.has(c.id)).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button onClick={link} disabled={!linkTo || busy} style={{ ...primaryBtn, opacity: linkTo ? 1 : 0.5 }}>Link</button>
      </div>
      {confirmDelete && (
        <ConfirmDialog title={`Delete ${contact.name}?`} onConfirm={del} onCancel={() => setConfirmDelete(false)}>
          {tracks.length ? `This also removes them from ${tracks.length} client${tracks.length === 1 ? "" : "s"}’ CRM, including their notes.` : "They’re removed from the shared contact book."}
        </ConfirmDialog>
      )}
    </Modal>
  );
}
